import React, { useState } from 'react';
import { FloraElements } from './DecorativeElements';
import { useIntersectionObserver } from '../utils/animations';
import { Check, Heart, Leaf, Loader2, Music } from 'lucide-react';
import { guardarRSVPEnFirebase } from '../services/firebaseService';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import BotanicalBackground from './BotanicalBackground';
import { useToast } from '@/hooks/use-toast';

const rsvpFormSchema = z.object({
  "Nombre": z.string().min(1, { message: "Por favor, ingresa tu nombre" }),
  "Asistencia": z.enum(["Sí asistiré", "No podré asistir"], {
    required_error: "Por favor, indica si asistirás",
  }),
  "Alergias/Preferencias": z.string().optional().default(""),
  "Recomendaciones Musicales": z.string().optional().default(""),
});

type RsvpFormValues = z.infer<typeof rsvpFormSchema>;

const RSVP: React.FC = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const { ref: titleRef, isVisible: titleVisible } = useIntersectionObserver({ threshold: 0.5, rootMargin: '0px' });
  const { ref: formRef, isVisible: formVisible } = useIntersectionObserver({ threshold: 0.2, rootMargin: '0px' });
  
  const form = useForm<RsvpFormValues>({
    resolver: zodResolver(rsvpFormSchema),
    defaultValues: {
      "Nombre": "",
      "Asistencia": undefined,
      "Alergias/Preferencias": "",
      "Recomendaciones Musicales": "",
    },
  });

  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (data: RsvpFormValues) => {
    console.log("Form data:", data);
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Mostrar mensaje de "Enviando confirmación..."
      toast({
        title: 'Enviando confirmación...',
        description: 'Estamos guardando tu respuesta.',
        variant: 'default',
      });
      
      // Convertir la opción de asistencia al formato requerido ("sí" o "no")
      const asistencia = data.Asistencia === "Sí asistiré" ? "sí" : "no";
      
      // Guardar directamente en Firebase con el nuevo esquema
      const result = await guardarRSVPEnFirebase({
        nombre: data.Nombre.trim(),
        asistencia: asistencia,
        preferencias: data["Alergias/Preferencias"] || "",
        canciones: data["Recomendaciones Musicales"] || ""
      });
      
      if (result) {
        setIsSubmitted(true);
        form.reset();
        
        // Mostrar mensaje de éxito
        toast({
          title: '¡Gracias por confirmar!',
          description: 'Tu respuesta ha sido registrada correctamente.',
          variant: 'success',
        });
        
        // Resetear el estado después de 3 segundos
        setTimeout(() => {
          setIsSubmitted(false);
        }, 3000);
      } else {
        setError('Ha ocurrido un error al enviar tu RSVP. Por favor, intenta nuevamente.');
        
        // Mostrar mensaje de error
        toast({
          title: 'Error',
          description: 'No se pudo guardar tu respuesta. Por favor, intenta nuevamente.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error al guardar RSVP:', error);
      setError(error instanceof Error ? error.message : 'Ocurrió un error al enviar tu respuesta');
      
      // Mostrar mensaje de error
      toast({
        title: 'Error',
        description: 'Ocurrió un problema al procesar tu solicitud. Por favor, intenta nuevamente.',
        variant: 'destructive',
      });
      
      // Ocultar el mensaje de error después de 5 segundos
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="rsvp" className="relative py-20 px-4">
      <div className="absolute inset-0 overflow-hidden">
        <BotanicalBackground section="registry" className="absolute h-full" />
      </div>
      
      <div className="max-w-3xl mx-auto relative z-10">
        <div 
          ref={titleRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-16 transition-all duration-700 transform ${
            titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-parisienne text-forest mb-6">RSVP</h2>
          <p className="text-lg font-baskerville text-forest/80 mb-6">
            Esperamos compartir contigo este día tan especial
          </p>
          <div className="w-24 h-0.5 bg-sage/30 mx-auto"></div>
        </div>
        
        <div 
          ref={formRef as React.RefObject<HTMLDivElement>}
          className={`glass-card p-6 md:p-8 bg-white/80 backdrop-blur-md rounded-lg shadow-md transition-all duration-700 transform ${
            formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {isSubmitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-sage/40 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-forest" />
              </div>
              <h3 className="text-2xl font-baskerville text-forest mb-4">¡Gracias por confirmar!</h3>
              <p className="text-forest/80">
                Hemos recibido tu respuesta y nos comunicaremos contigo si necesitamos algún detalle adicional.
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-2xl font-baskerville text-forest mb-4">Hubo un problema</h3>
              <p className="text-red-500 mb-6">{error}</p>
              <button 
                onClick={() => setError(null)}
                className="px-4 py-2 bg-forest text-white rounded-md hover:bg-forest/80 transition-colors"
              >
                Intentar nuevamente
              </button>
            </div>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="Nombre"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-baskerville text-forest/80">Nombre</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Escribe tu nombre completo" 
                            {...field} 
                            className="bg-white/80 border-sage/60 focus:ring-forest/40"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="Asistencia"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <FormLabel className="text-sm font-baskerville text-forest/80">¿Podrás acompañarnos?</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex gap-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="Sí asistiré" id="attendance-yes" className="accent-forest" />
                              <Label htmlFor="attendance-yes">Sí asistiré</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="No podré asistir" id="attendance-no" className="accent-forest" />
                              <Label htmlFor="attendance-no">No podré asistir</Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                      <FormField
                        control={form.control}
                    name="Alergias/Preferencias"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-baskerville text-forest/80">
                              ¿Tienes alguna alergia alimentaria o preferencia especial?
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                className="bg-white/80 border-sage/60 focus:ring-forest/40 h-24"
                                placeholder="Escríbenos si hay algo que debamos tener en cuenta"
                                {...field}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                    name="Recomendaciones Musicales"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-baskerville text-forest/80">
                              <span className="flex items-center gap-2">
                                <Music className="w-4 h-4 text-sage" />
                                ¿Qué canciones te gustaría escuchar en nuestra fiesta?
                              </span>
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                className="bg-white/80 border-sage/60 focus:ring-forest/40 h-24"
                                placeholder="Recomiéndanos tus canciones favoritas para bailar"
                                {...field}
                              />
                            </FormControl>
                            <FormDescription className="text-xs text-forest/60 italic">
                              Puedes sugerir varias canciones separándolas con comas
                            </FormDescription>
                          </FormItem>
                        )}
                      />
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 bg-forest text-cream rounded-md hover:bg-forest/90 transition-colors duration-300 flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <span>Confirmar asistencia ♡</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </Form>
          )}
        </div>
        
        <div className="text-center mt-16">
        </div>
      </div>
      
      <div className="absolute left-0 top-0 z-0 pointer-events-none overflow-hidden" style={{maxWidth: '25%'}}>
        <FloraElements position="topleft" variant="rsvp" />
      </div>
      <div className="absolute right-0 bottom-0 z-0 pointer-events-none overflow-hidden" style={{maxWidth: '25%'}}>
        <FloraElements position="bottomright" variant="rsvp" />
      </div>
    </section>
  );
};

export default RSVP;
